import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Minus, ClipboardCheck, Map, FileText, Calendar, HardHat, Search, FileImage, Repeat } from "lucide-react";
import PageHero from "../components/PageHero";
import { useMedia } from "../hooks/useMedia";

const STEPS = [
  {
    n: "01",
    title: "Request Quote",
    Icon: ClipboardCheck,
    lead: "Twelve seconds on the pricing engine — or a two-line email.",
    body:
      "Our instant-price tool converts square footage, story count, and frequency into a fixed monthly, quarterly, or annual retainer. If your property is unusual (historic landmarks, dual-frontage, atria, complex signage), the two-line email routes to an ops director within one business hour.",
  },
  {
    n: "02",
    title: "Site Assessment",
    Icon: Map,
    lead: "Feet on the property. Every façade measured, every risk flagged.",
    body:
      "Within seventy-two hours we walk the site with the property manager or superintendent. We measure linear feet of window, square footage of façade, count awnings, striping cells, and enclosures. We photograph the pre-service condition and note any structural concerns for the proposal.",
  },
  {
    n: "03",
    title: "Custom Proposal",
    Icon: FileText,
    lead: "A written scope of work, unit-priced, delivered inside 24 hours.",
    body:
      "The proposal is a formal PDF with itemised scope by discipline, unit pricing, cadence recommendation, and a proposed service calendar. No verbal handshakes. No 'we'll figure it out.' Every line is signed off before we ever leave a truck at your curb.",
  },
  {
    n: "04",
    title: "Scheduling",
    Icon: Calendar,
    lead: "Fifteen-minute arrival window. Locked in your calendar.",
    body:
      "Service is scheduled around your tenants, not ours. We work at dawn for restaurants, overnight for retail flagships, and off-peak weekends for medical offices. You receive a calendar invite with the fifteen-minute arrival window plus lead-tech contact.",
  },
  {
    n: "05",
    title: "Professional Service",
    Icon: HardHat,
    lead: "Uniformed crew. Commercial-grade rig. Nothing improvised.",
    body:
      "The crew arrives in matte-black wraps, uniformed with laminated IDs, and starts work exactly at the window opening. We carry $2M general liability, $1M umbrella, auto, and workers-comp — proof of insurance is emailed to your risk team the morning of service.",
  },
  {
    n: "06",
    title: "Quality Inspection",
    Icon: Search,
    lead: "Every façade re-walked before the truck rolls.",
    body:
      "The lead tech walks the site with the crew, checking every pane, every stripe, every corner against the proposal's scope. Anything sub-standard is redone on the spot. We do not leave until it matches the pre-signed brief.",
  },
  {
    n: "07",
    title: "Digital Service Report",
    Icon: FileImage,
    lead: "Time-stamped, geotagged photos in your inbox before we roll.",
    body:
      "You receive a full PDF service report — technician names, time on site, checklist executed, before/after geotagged photos of every façade, and any recommended future work. If it's not documented in the report, it wasn't billed.",
  },
  {
    n: "08",
    title: "Recurring Maintenance Plan",
    Icon: Repeat,
    lead: "The retainer runs itself. You never place another call.",
    body:
      "Monthly, quarterly, or seasonal service auto-schedules on your calendar. Weather events within the SLA window trigger an automatic re-clean. Every visit compounds a service history that you can hand to your incoming property manager if you ever change hands.",
  },
];

function Step({ s, i, open, toggle }) {
  const isOpen = open === i;
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, delay: i * 0.05 }}
      className="relative"
      data-testid={`process-step-${s.n}`}
    >
      <div className="grid md:grid-cols-[100px_60px_1fr_auto] items-start gap-5 md:gap-8 py-8" style={{ borderTop: "1px solid var(--line)" }}>
        <div className="font-mono text-[11px] uppercase tracking-[0.4em] pt-2" style={{ color: "var(--gold)" }}>{s.n}</div>
        <div className="pt-1">
          <div className="w-12 h-12 flex items-center justify-center" style={{ border: "1px solid var(--line-gold)", borderRadius: "var(--r-full)", background: "rgba(198,168,124,0.06)" }}>
            <s.Icon size={16} strokeWidth={1.4} style={{ color: "var(--gold)" }} />
          </div>
        </div>
        <div>
          <button
            onClick={() => toggle(isOpen ? -1 : i)}
            className="text-left w-full group"
            data-cursor="hover"
            data-testid={`process-toggle-${s.n}`}
          >
            <h3 className="font-display text-3xl md:text-4xl group-hover:text-[var(--gold-2)] transition-colors duration-500" style={{ letterSpacing: "-0.025em" }}>{s.title}</h3>
            <p className="mt-3 font-body" style={{ color: "var(--text-2)", lineHeight: 1.6, fontSize: 16 }}>{s.lead}</p>
          </button>
          <AnimatePresence>
            {isOpen && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="mt-6 font-body overflow-hidden max-w-3xl"
                style={{ color: "var(--text-2)", lineHeight: 1.8, fontSize: 17 }}
              >
                {s.body}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
        <button
          onClick={() => toggle(isOpen ? -1 : i)}
          className="w-11 h-11 flex items-center justify-center transition-all"
          style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)", color: "var(--gold)" }}
          data-cursor="hover"
          aria-label={isOpen ? "Collapse" : "Expand"}
        >
          {isOpen ? <Minus size={15} /> : <Plus size={15} />}
        </button>
      </div>
    </motion.div>
  );
}

export default function Process() {
  const { url } = useMedia();
  const [open, setOpen] = useState(0);

  return (
    <div className="min-h-screen" data-testid="process-page">
      <PageHero
        eyebrow="The Bespoke Process"
        title={<>Eight steps. <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Zero improvisation.</span></>}
        subtitle="Every property manager we serve gets the same protocol — the one refined over eleven years, four hundred contracts, and zero callbacks last quarter."
        image={url("process-hero") || url("outdoor-lighting")}
        kicker="From first click to signed service report."
      />

      <section className="py-24 md:py-32" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
          {STEPS.map((s, i) => (
            <Step key={s.n} s={s} i={i} open={open} toggle={setOpen} />
          ))}
          <div style={{ borderTop: "1px solid var(--line)" }} />
        </div>
      </section>

      <section className="py-28 md:py-36 text-center" style={{ background: "var(--bg-2)" }}>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95]" style={{ letterSpacing: "-0.035em" }}>
          The <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>protocol,</span> now yours.
        </h2>
        <div className="mt-12 flex flex-wrap justify-center gap-5">
          <Link to="/quote" className="btn-glow" data-testid="process-cta-quote" data-cursor="hover">
            Start Your Quote <ArrowUpRight size={13} />
          </Link>
          <Link to="/audit" className="inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-2)" }} data-testid="process-cta-audit">
            <span className="inline-flex items-center justify-center w-10 h-10" style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)" }}>
              <ArrowUpRight size={12} style={{ color: "var(--gold)" }} />
            </span>
            Book a walk-through
          </Link>
        </div>
      </section>
    </div>
  );
}
